import { initializeTelemetry } from '@if/core/telemetry'
import type { FeatureSwitchInfo } from '@if/core/types'
import { GlobalDesignSystemProvider, initializeAuth } from '@if/mui'
import { QueryClient } from '@tanstack/react-query'
import { createRoot } from 'react-dom/client'
import { APP_VERSION } from './appVersion'
import { buildAuthConfig, mapClaims } from './authConfig'
import App from './containers/App/App'
import { getApiClient } from './data/api/api-client'
import './index.css'
import initializeAppInsights from './services/AppInsights'
import { businessErrorRegistry } from './services/business-error-registry'
import { reportErrorToConsole, setupTracking } from './services/error-logger'
import './types/enumSets'

// Before React renders. A build with no connection string makes this a no-op, so
// local dev is unaffected. `APP_VERSION` rather than `import.meta.env.VITE_VERSION`:
// the bundle bakes that at build time and it is empty in deployed builds — the
// `<meta name="version">` tag is the real source (see appVersion.ts).
initializeTelemetry({
  connectionString: import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING,
  appName: 'merchant-dashboard',
  appVersion: APP_VERSION,
})

const ai = initializeAppInsights()
setupTracking(ai)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

// The feature-switch fetch backing @if/mui's FeatureSwitchProvider (runs
// authenticated, below the auth gate).
const fetchFeatureSwitches = (): Promise<FeatureSwitchInfo> =>
  getApiClient()
    .get<FeatureSwitchInfo>('/FeatureSwitch')
    .then((r) => r.data)

const root = createRoot(document.getElementById('root')!)

async function bootstrap() {
  const auth = await initializeAuth(buildAuthConfig())
  // `null` → a forgot-password redirect is in flight; render nothing.
  if (!auth) return

  root.render(
    <GlobalDesignSystemProvider
      auth={auth}
      mapClaims={mapClaims}
      queryClient={queryClient}
      fetchFeatureSwitches={fetchFeatureSwitches}
      error={{ registry: businessErrorRegistry }}
    >
      <App />
    </GlobalDesignSystemProvider>,
  )
}

bootstrap().catch(reportErrorToConsole)
